/**
 * demoUsers.js
 * ============
 * Pre-seeded local accounts for the My Rock Tools PWA.
 *
 * These are NOT cryptographic credentials — the passwords are visible in the
 * JS bundle. They provide a lightweight gate for demo/testing environments
 * until Azure AD SSO (employees) and Azure B2C (customers) are wired up.
 *
 * Roles:
 *   employee   — Sandvik internal user via Azure AD SSO. Full access incl. admin.
 *   customer   — B2C external user. Can browse, order, manage profile. Name/email locked.
 *   guest      — Unauthenticated visitor. Catalog browsing only, no portal features.
 *
 * Demo customer: BrightStars Incorp. (BRST-4210)
 *
 * @module demoUsers
 */

export const DEMO_USERS = [
  {
    id: 'vandana_employee',
    name: 'Vandana Kabilan',
    email: 'vandana.kabilan@sandvik.com',
    password: 'MrtAdmin@26',
    roleKey: 'employee',
    label: 'Vandana Kabilan (Employee / Admin)',
    account: {
      company: 'Sandvik Rock Tools',
      country: 'Canada',
      phone: '+1 416-555-0100',
      shippingAddress: '123 Mining Rd, Sudbury ON P3A 4T2',
      salesArea: 'Canada',
      salesEntity: '4782',
      salesEntityDisplay: 'SD-Canada',
      customerName: 'BRST-4210 BRIGHTSTARS INCORP.',
      customerId: 'BRST-4210',
      nickname: 'Vandana',
      avatarUrl: null,
    },
    permissions: ['read', 'write', 'admin', 'order', 'view_prices', 'export_cart', 'reports', 'cases'],
  },
  {
    id: 'b2c_customer',
    name: 'Jean-Marc Beaulieu',
    email: 'jm.beaulieu@brightstars.ca',
    password: 'MrtCust@26',
    roleKey: 'customer',
    label: 'Jean-Marc Beaulieu (BrightStars Incorp.)',
    account: {
      company: 'BrightStars Incorp.',
      country: 'Canada',
      phone: '+1 705-555-0340',
      shippingAddress: '780 Goldmine Blvd, Timmins ON P4N 7K3',
      salesArea: 'Canada',
      salesEntity: '4782',
      salesEntityDisplay: 'SD-Canada',
      customerName: 'BRST-4210 BRIGHTSTARS INCORP.',
      customerId: 'BRST-4210',
      nickname: 'Jean-Marc',
      avatarUrl: null,
    },
    permissions: ['read', 'write', 'order', 'view_prices', 'export_cart', 'cases'],
  },
  {
    id: 'guest_user',
    name: 'Guest Visitor',
    email: 'guest@example.com',
    password: 'MrtGuest@26',
    roleKey: 'guest',
    label: 'Guest (Browse only)',
    account: {
      company: '',
      country: '',
      phone: '',
      shippingAddress: '',
      salesArea: '',
      salesEntity: '',
      salesEntityDisplay: '',
      customerName: '',
      customerId: '',
      nickname: 'Guest',
      avatarUrl: null,
    },
    permissions: ['read'],
  },
];

/**
 * Authenticate a local login attempt.
 * @param {string} email - Email address (case-insensitive)
 * @param {string} password - Password (exact match)
 * @returns {{ ok: boolean, user?: Object, error?: string }}
 */
export function authenticateLocal(email, password) {
  if (!email || !password) {
    return { ok: false, error: 'Email and password are required.' };
  }
  const match = DEMO_USERS.find(
    u => u.email.toLowerCase() === email.trim().toLowerCase()
  );
  if (!match) {
    return { ok: false, error: 'No account found for this email.' };
  }
  if (match.password !== password) {
    return { ok: false, error: 'Incorrect password.' };
  }
  return {
    ok: true,
    user: {
      id: match.id,
      name: match.name,
      email: match.email,
      roleKey: match.roleKey,
      authMode: 'local',
      ...match.account,
    },
    permissions: match.permissions,
  };
}

/**
 * Resolve a role key from an email address.
 * Falls back to 'guest' for unknown emails.
 * @param {string} email
 * @returns {string}
 */
export function roleKeyForEmail(email) {
  const match = DEMO_USERS.find(
    u => u.email.toLowerCase() === (email || '').trim().toLowerCase()
  );
  return match?.roleKey || 'guest';
}
